import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  BarChart3,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Wallet,
  AlertTriangle,
  Clock,
  Landmark,
  PiggyBank,
  Receipt,
  Inbox,
  Calendar,
  Info,
  Repeat,
  CreditCard,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { supabase } from '../lib/supabase';
import { CREDITED_PAYMENT_STATUS } from '../lib/creditNotes';
import { useNotifications } from '../store/useStore';
import { formatCurrency, formatDate } from '../lib/exportUtils';
import { cn } from '../lib/cn';
import {
  computeForfettario,
  computeF24Schedule,
  loadForfettarioParams,
  saveForfettarioParams,
  type ForfettarioParams,
} from '../lib/forfettarioUtils';
import { getSubscriptionStatus } from '../lib/subscriptionUtils';

const MESI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

// Solo documenti fiscalmente rilevanti per il fatturato (proforma e DDT esclusi).
const FISCAL_TYPES = new Set(['invoice', 'invoice_with_delivery', 'credit_note']);

interface Installment {
  amount: number | null;
  due_date: string | null;
  paid_at: string | null;
  payment_status: string | null;
  installment_number: number | null;
}

interface InvoiceRow {
  id: string;
  invoice_number: string | null;
  invoice_date: string;
  document_type: string;
  status: string;
  payment_status: string | null;
  payment_due_date: string | null;
  total_amount: number | null;
  taxable_amount: number | null;
  customers: { company_name: string | null } | null;
  invoice_installments: Installment[] | null;
}

interface PassiveInvoice {
  total: number | null;
  date: string | null;
}

interface SubscriptionRow {
  monthly_amount: number | null;
  status: string;
  next_renewal_date: string | null;
}

interface OpenScadenza {
  invoiceId: string;
  invoiceNumber: string | null;
  customer: string;
  amount: number;
  dueDate: string | null;
  overdue: boolean;
}

const yearOf = (d: string | null | undefined): number | null => {
  if (!d) return null;
  const y = Number(String(d).slice(0, 4));
  return Number.isFinite(y) ? y : null;
};
const monthOf = (d: string | null | undefined): number | null => {
  if (!d) return null;
  const m = Number(String(d).slice(5, 7));
  return Number.isFinite(m) && m >= 1 && m <= 12 ? m - 1 : null;
};

export default function AnalisiAziendaliPage() {
  const { addNotification } = useNotifications();

  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [passive, setPassive] = useState<PassiveInvoice[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([]);
  const [passiveError, setPassiveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const [year, setYear] = useState<number>(currentYear);

  const [params, setParams] = useState<ForfettarioParams>(() => loadForfettarioParams());
  const [taxBaseMode, setTaxBaseMode] = useState<'incassato' | 'emesso'>('incassato');
  const [view, setView] = useState<'panoramica' | 'tasse'>('panoramica');

  const updateParam = (patch: Partial<ForfettarioParams>) => {
    setParams((prev) => {
      const next = { ...prev, ...patch };
      saveForfettarioParams(next);
      return next;
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setPassiveError(null);

    // Fatture attive emesse + relative scadenze
    const invQuery = supabase
      .from('invoices')
      .select(
        'id, invoice_number, invoice_date, document_type, status, payment_status, payment_due_date, total_amount, taxable_amount, customers(company_name), invoice_installments(amount, due_date, paid_at, payment_status, installment_number)'
      )
      .eq('status', 'issued');

    // Fatture passive (A-Cube, via edge function) — best effort
    const passivePromise = supabase.functions.invoke('passive-invoices-list', { body: {} });

    // Subscriptions attive (ricavo ricorrente — solo informativo)
    const subQuery = supabase
      .from('customer_subscriptions')
      .select('monthly_amount, status, next_renewal_date');

    const [{ data: invData, error: invError }, passiveRes, { data: subData }] = await Promise.all([
      invQuery,
      passivePromise,
      subQuery,
    ]);

    if (invError) {
      addNotification({ type: 'error', title: 'Errore', message: 'Impossibile caricare le fatture attive' });
    } else {
      setInvoices(((invData as unknown) as InvoiceRow[]).filter((i) => FISCAL_TYPES.has(i.document_type)));
    }

    setSubscriptions(((subData as unknown) as SubscriptionRow[]) || []);

    const pErr = (passiveRes.error as any) || (passiveRes.data as any)?.error;
    if (pErr) {
      setPassive([]);
      setPassiveError(typeof pErr === 'string' ? pErr : pErr.message || 'Fatture passive non disponibili');
    } else {
      setPassive(((passiveRes.data as any)?.invoices ?? []) as PassiveInvoice[]);
    }

    setLoading(false);
  }, [addNotification]);

  useEffect(() => {
    load();
  }, [load]);

  const todayStr = useMemo(() => now.toISOString().slice(0, 10), [now]);

  // ---- Aggregazione per anno/mese ----
  const data = useMemo(() => {
    const emessoByYear = new Map<number, number[]>();
    const incassatoByYear = new Map<number, number[]>();
    const passiveByYear = new Map<number, number[]>();
    const openScadenze: OpenScadenza[] = [];
    let openScaduto = 0;
    let openFuturo = 0;

    const bucket = (map: Map<number, number[]>, y: number) => {
      let arr = map.get(y);
      if (!arr) {
        arr = new Array(12).fill(0);
        map.set(y, arr);
      }
      return arr;
    };

    for (const inv of invoices) {
      const sign = inv.document_type === 'credit_note' ? -1 : 1;
      const total = inv.total_amount || 0;
      const taxable = (inv.taxable_amount || 0) * sign;

      // Fatturato emesso (imponibile) — competenza, per data fattura
      const ey = yearOf(inv.invoice_date);
      const em = monthOf(inv.invoice_date);
      if (ey !== null && em !== null) bucket(emessoByYear, ey)[em] += taxable;

      // Rate: usa le scadenze se presenti, altrimenti sintetizza dalla fattura
      let rates: Installment[] = inv.invoice_installments && inv.invoice_installments.length > 0
        ? inv.invoice_installments
        : [{
            amount: total,
            due_date: inv.payment_due_date || inv.invoice_date,
            paid_at: null,
            payment_status: inv.payment_status === 'paid' ? 'paid' : 'pending',
            installment_number: 1,
          }];

      for (const r of rates) {
        const rateAmount = (r.amount || 0) * sign;
        // quota di imponibile corrispondente a questa rata
        const imponibileQuota = total > 0 ? taxable * ((r.amount || 0) / total) : 0;
        const paid = r.payment_status === 'paid';

        if (paid) {
          const paidDate = r.paid_at || r.due_date || inv.invoice_date;
          const py = yearOf(paidDate);
          const pm = monthOf(paidDate);
          if (py !== null && pm !== null) bucket(incassatoByYear, py)[pm] += imponibileQuota;
        } else if (sign > 0 && inv.payment_status !== CREDITED_PAYMENT_STATUS) {
          // Solo le fatture (non note di credito) generano crediti da incassare;
          // una fattura stornata del tutto non ha più nulla da incassare.
          const overdue = !!r.due_date && r.due_date < todayStr;
          if (overdue) openScaduto += rateAmount;
          else openFuturo += rateAmount;
          openScadenze.push({
            invoiceId: inv.id,
            invoiceNumber: inv.invoice_number,
            customer: inv.customers?.company_name || '—',
            amount: rateAmount,
            dueDate: r.due_date,
            overdue,
          });
        }
      }
    }

    for (const p of passive) {
      const py = yearOf(p.date);
      const pm = monthOf(p.date);
      if (py !== null && pm !== null) bucket(passiveByYear, py)[pm] += p.total || 0;
    }

    const years = new Set<number>([currentYear]);
    [emessoByYear, incassatoByYear, passiveByYear].forEach((m) => m.forEach((_v, k) => years.add(k)));

    openScadenze.sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));

    return {
      emessoByYear,
      incassatoByYear,
      passiveByYear,
      availableYears: Array.from(years).sort((a, b) => b - a),
      openScadenze,
      openScaduto,
      openFuturo,
      openTotale: openScaduto + openFuturo,
    };
  }, [invoices, passive, currentYear, todayStr]);

  const emessoMonths = data.emessoByYear.get(year) || new Array(12).fill(0);
  const incassatoMonths = data.incassatoByYear.get(year) || new Array(12).fill(0);
  const passiveMonths = data.passiveByYear.get(year) || new Array(12).fill(0);

  const sum = (arr: number[]) => arr.reduce((s, v) => s + v, 0);
  const totEmesso = sum(emessoMonths);
  const totIncassato = sum(incassatoMonths);
  const totPassive = sum(passiveMonths);
  const margineCassa = totIncassato - totPassive;

  const chartMax = Math.max(1, ...emessoMonths, ...incassatoMonths, ...passiveMonths);

  // ---- Ricavo ricorrente subscriptions (solo informativo, NON sommato al fatturato) ----
  const subStats = useMemo(() => {
    const active = subscriptions.filter((s) => s.status === 'active');
    const mrr = active.reduce((s, x) => s + (x.monthly_amount || 0), 0);
    const warning = active.filter(
      (s) => s.next_renewal_date && getSubscriptionStatus(s.next_renewal_date) === 'warning'
    ).length;
    return { count: active.length, mrr, arr: mrr * 12, warning };
  }, [subscriptions]);

  // ---- Previsione tasse forfettario ----
  const taxBase = taxBaseMode === 'incassato' ? totIncassato : totEmesso;
  const forecast = useMemo(() => computeForfettario(taxBase, params), [taxBase, params]);

  // Proiezione a fine anno (solo se anno corrente e su base incassato)
  const monthsElapsed = year === currentYear ? now.getMonth() + 1 : 12;
  const annualizedBase = monthsElapsed > 0 ? (taxBase / monthsElapsed) * 12 : taxBase;
  const forecastProjected = useMemo(
    () => computeForfettario(annualizedBase, params),
    [annualizedBase, params]
  );
  const showProjection = year === currentYear && monthsElapsed < 12 && taxBase > 0;

  // Calendario versamenti F24 (su base proiettata se anno corrente in corso, altrimenti sul dovuto)
  const taxDueForCalendar = showProjection ? forecastProjected.totale : forecast.totale;
  const f24 = useMemo(() => computeF24Schedule(taxDueForCalendar, year), [taxDueForCalendar, year]);

  const kpis = [
    { label: `Fatturato ${year}`, value: formatCurrency(totEmesso), icon: Receipt, color: 'text-indigo-500', hint: 'Imponibile emesso (competenza)' },
    { label: `Incassato ${year}`, value: formatCurrency(totIncassato), icon: Wallet, color: 'text-green-500', hint: 'Per data pagamento (cassa)' },
    { label: `Costi ${year}`, value: formatCurrency(totPassive), icon: Inbox, color: 'text-pink-500', hint: 'Fatture passive ricevute' },
    { label: `Margine di cassa`, value: formatCurrency(margineCassa), icon: margineCassa >= 0 ? TrendingUp : TrendingDown, color: margineCassa >= 0 ? 'text-emerald-500' : 'text-red-500', hint: 'Incassato − costi' },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-blue-900">Analisi aziendali</h1>
          <p className="text-sm text-neutral-400 mt-1">
            Fatturato, incassi, costi e previsione tasse (regime forfettario)
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-10 w-28 bg-white border-gray-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {data.availableYears.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} className="h-10">
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Tabs Panoramica / Tasse */}
      <div className="flex gap-2">
        {([
          { key: 'panoramica', label: 'Panoramica', icon: BarChart3 },
          { key: 'tasse', label: 'Tasse', icon: Landmark },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setView(t.key)}
            className={cn(
              'px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2',
              view === t.key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {view === 'panoramica' && (
      <>
      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {kpis.map((kpi, i) => (
          <Card key={i} className="border-gray-200 bg-white">
            <CardContent className="p-3 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] md:text-sm font-medium text-neutral-500 uppercase tracking-wider">{kpi.label}</p>
                <kpi.icon className={cn('h-3 w-3 md:h-4 md:w-4', kpi.color)} />
              </div>
              <div className="text-sm md:text-2xl font-bold text-gray-900 truncate">{kpi.value}</div>
              <p className="text-[10px] text-neutral-400 mt-1 hidden md:block">{kpi.hint}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {passiveError && (
        <p className="text-xs text-amber-600 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> Fatture passive non caricate: {passiveError}
        </p>
      )}

      {/* Crediti da incassare (snapshot attuale) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
        <Card className="border-gray-200 bg-white">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Da incassare</p>
              <Clock className="h-4 w-4 text-blue-500" />
            </div>
            <div className="text-xl md:text-2xl font-bold text-gray-900">{formatCurrency(data.openTotale)}</div>
            <p className="text-[10px] text-neutral-400 mt-1">Crediti aperti (tutte le annualità)</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-red-600 uppercase tracking-wider">Scaduto</p>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </div>
            <div className="text-xl md:text-2xl font-bold text-red-700">{formatCurrency(data.openScaduto)}</div>
            <p className="text-[10px] text-red-500 mt-1">Scadenze già superate, non saldate</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-wider">A scadere</p>
              <Calendar className="h-4 w-4 text-amber-500" />
            </div>
            <div className="text-xl md:text-2xl font-bold text-amber-700">{formatCurrency(data.openFuturo)}</div>
            <p className="text-[10px] text-amber-500 mt-1">Scadenze future ancora aperte</p>
          </CardContent>
        </Card>
      </div>

      {/* Cashflow mensile */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
            <div>
              <h2 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-indigo-500" /> Andamento mensile {year}
              </h2>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-indigo-500" /> Emesso</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-green-500" /> Incassato</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-pink-500" /> Costi</span>
            </div>
          </div>

          <div className="flex items-end gap-1 md:gap-2 h-40 md:h-52">
            {MESI.map((m, i) => (
              <div key={m} className="flex-1 flex flex-col items-center gap-1 h-full justify-end group">
                <div className="flex items-end justify-center gap-0.5 w-full h-full">
                  {[
                    { v: emessoMonths[i], c: 'bg-indigo-500' },
                    { v: incassatoMonths[i], c: 'bg-green-500' },
                    { v: passiveMonths[i], c: 'bg-pink-500' },
                  ].map((bar, bi) => (
                    <div
                      key={bi}
                      className={cn('w-1/4 rounded-t transition-all', bar.c)}
                      style={{ height: `${Math.max(0, (Math.abs(bar.v) / chartMax) * 100)}%` }}
                      title={`${m}: ${formatCurrency(bar.v)}`}
                    />
                  ))}
                </div>
                <span className="text-[9px] md:text-[10px] text-neutral-400">{m}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Ricavo ricorrente (subscriptions) — informativo */}
      {subStats.count > 0 && (
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-2 mb-1">
              <Repeat className="w-5 h-5 text-teal-600" />
              <h2 className="text-base md:text-lg font-bold text-gray-900">Ricavo ricorrente (subscriptions)</h2>
            </div>
            <div className="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-200 p-3 text-xs text-sky-700 mb-5">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Dato <strong>informativo</strong>: il ricavo dei canoni è già incluso in Fatturato/Incassato quando
                emetti la relativa fattura. Non è sommato qui né nella base imponibile per evitare il doppio conteggio.
              </span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              {[
                { label: 'MRR', value: formatCurrency(subStats.mrr), icon: Repeat, color: 'text-teal-500', hint: 'Ricavo ricorrente mensile' },
                { label: 'ARR', value: formatCurrency(subStats.arr), icon: TrendingUp, color: 'text-emerald-500', hint: 'Annuo (MRR × 12)' },
                { label: 'Subs attive', value: subStats.count, icon: CreditCard, color: 'text-blue-500', hint: 'Sottoscrizioni in corso' },
                { label: 'In scadenza', value: subStats.warning, icon: AlertTriangle, color: 'text-amber-500', hint: 'Rinnovo entro 30 gg' },
              ].map((kpi, i) => (
                <div key={i} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 md:p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] md:text-xs font-medium text-neutral-500 uppercase tracking-wider">{kpi.label}</p>
                    <kpi.icon className={cn('h-3 w-3 md:h-4 md:w-4', kpi.color)} />
                  </div>
                  <div className="text-sm md:text-xl font-bold text-gray-900 truncate">{kpi.value}</div>
                  <p className="text-[10px] text-neutral-400 mt-1 hidden md:block">{kpi.hint}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      </>
      )}

      {view === 'tasse' && (
      <>
      {/* Previsione tasse forfettario */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center gap-2 mb-1">
            <Landmark className="w-5 h-5 text-emerald-600" />
            <h2 className="text-base md:text-lg font-bold text-gray-900">Previsione tasse — Regime forfettario</h2>
          </div>
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700 mb-5">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Stima indicativa basata sul principio di cassa (si tassa l'incassato). I parametri dipendono dal codice ATECO
              e dalla situazione del contribuente: verificali con il commercialista. Non sostituisce la dichiarazione fiscale.
            </span>
          </div>

          {/* Parametri */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-5">
            <div className="space-y-1.5">
              <Label className="text-[10px] text-neutral-500 uppercase tracking-wider">Base di calcolo</Label>
              <Select value={taxBaseMode} onValueChange={(v) => setTaxBaseMode(v as 'incassato' | 'emesso')}>
                <SelectTrigger className="h-9 bg-white border-gray-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="incassato">Incassato (cassa)</SelectItem>
                  <SelectItem value="emesso">Emesso (competenza)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-neutral-500 uppercase tracking-wider">Coeff. redditività %</Label>
              <Input
                type="number"
                value={params.coefficiente}
                onChange={(e) => updateParam({ coefficiente: Number(e.target.value) })}
                className="h-9 bg-white border-gray-200"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-neutral-500 uppercase tracking-wider">Imposta sostitutiva</Label>
              <Select value={String(params.aliquotaImposta)} onValueChange={(v) => updateParam({ aliquotaImposta: Number(v) })}>
                <SelectTrigger className="h-9 bg-white border-gray-200"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5% (start-up, primi 5 anni)</SelectItem>
                  <SelectItem value="15">15% (a regime)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] text-neutral-500 uppercase tracking-wider">Contributi INPS %</Label>
              <Input
                type="number"
                step="0.01"
                value={params.aliquotaInps}
                onChange={(e) => updateParam({ aliquotaInps: Number(e.target.value) })}
                className="h-9 bg-white border-gray-200"
              />
            </div>
          </div>

          {/* Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
              {[
                { label: `Base imponibile (${taxBaseMode === 'incassato' ? 'incassato' : 'emesso'})`, value: forecast.base },
                { label: `Reddito lordo (× ${params.coefficiente}%)`, value: forecast.redditoLordo },
                { label: `Contributi INPS (${params.aliquotaInps}%)`, value: forecast.contributiInps, neg: true },
                { label: 'Reddito imponibile netto', value: forecast.redditoImponibile },
                { label: `Imposta sostitutiva (${params.aliquotaImposta}%)`, value: forecast.impostaSostitutiva, neg: true },
              ].map((row, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                  <span className="text-neutral-600">{row.label}</span>
                  <span className={cn('font-semibold', row.neg ? 'text-red-600' : 'text-gray-900')}>
                    {row.neg ? '− ' : ''}{formatCurrency(row.value)}
                  </span>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-4">
              <div className="rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white p-5 shadow-lg">
                <div className="flex items-center gap-2 mb-1">
                  <PiggyBank className="w-5 h-5" />
                  <p className="text-xs uppercase tracking-wider opacity-90">Totale da accantonare {year}</p>
                </div>
                <div className="text-3xl font-black">{formatCurrency(forecast.totale)}</div>
                <p className="text-xs opacity-90 mt-1">
                  Imposta {formatCurrency(forecast.impostaSostitutiva)} + INPS {formatCurrency(forecast.contributiInps)}
                  {' · '}≈ {forecast.pressioneEffettiva.toFixed(1)}% della base
                </p>
                <p className="text-xs opacity-90 mt-2 pt-2 border-t border-white/20">
                  Accantonamento consigliato: <strong>{formatCurrency(forecast.totale / 12)}</strong>/mese
                </p>
              </div>

              {showProjection && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                    <p className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Proiezione fine anno</p>
                  </div>
                  <p className="text-xs text-emerald-700">
                    Su base annualizzata ({formatCurrency(annualizedBase)} stimati su {monthsElapsed} mesi):
                  </p>
                  <div className="text-2xl font-bold text-emerald-800 mt-1">{formatCurrency(forecastProjected.totale)}</div>
                  <p className="text-[11px] text-emerald-600 mt-1">
                    da accantonare entro fine {year} (imposta + contributi stimati)
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Calendario versamenti F24 */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-5 h-5 text-blue-600" />
            <h2 className="text-base md:text-lg font-bold text-gray-900">Calendario versamenti F24 — {year}</h2>
          </div>
          <p className="text-xs text-neutral-400 mb-5">
            Quando versare la stima di {formatCurrency(taxDueForCalendar)} (imposta sostitutiva + INPS Gestione Separata).
            Acconto/saldo con metodo storico — stima a regime.
          </p>

          {f24.length === 0 ? (
            <p className="text-sm text-neutral-500">Nessun versamento stimato (base imponibile nulla).</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {f24.map((r, i) => (
                <div key={i} className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 flex items-center justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="flex flex-col items-center justify-center rounded-lg bg-blue-600 text-white px-3 py-2 flex-shrink-0">
                      <Calendar className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900">{r.date}</p>
                      <p className="text-xs text-blue-700 font-medium">{r.label}</p>
                      <p className="text-[11px] text-neutral-500 mt-0.5">{r.detail}</p>
                    </div>
                  </div>
                  <div className="text-lg font-black text-blue-800 whitespace-nowrap">{formatCurrency(r.amount)}</div>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-700 mt-5">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              Stima indicativa. Le date 2026 possono avere proroghe; il 30 giugno è prorogabile al 30 luglio
              (+0,40%). Saldo e acconti reali dipendono dall'anno precedente e dalla gestione previdenziale.
              Verifica sempre col commercialista.
            </span>
          </div>
        </CardContent>
      </Card>
      </>
      )}

      {view === 'panoramica' && (
      <>
      {/* Prossime scadenze incassi */}
      <Card className="bg-white border-gray-200 overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-500" />
          <h2 className="text-base md:text-lg font-bold text-gray-900">Scadenze incassi aperte</h2>
          <span className="text-xs text-neutral-400">({data.openScadenze.length})</span>
        </div>
        {data.openScadenze.length === 0 ? (
          <div className="p-8 text-center text-neutral-400 text-sm">Nessun credito aperto. Tutto incassato 🎉</div>
        ) : (
          <div className="table-scroll">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Fattura</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Cliente</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Scadenza</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Importo</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Stato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.openScadenze.slice(0, 30).map((s, i) => (
                  <tr key={`${s.invoiceId}-${i}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">{s.invoiceNumber || <span className="italic text-neutral-400">Bozza</span>}</td>
                    <td className="px-4 py-3 text-gray-600 truncate max-w-[200px]">{s.customer}</td>
                    <td className="px-4 py-3 text-gray-600">{s.dueDate ? formatDate(s.dueDate) : '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{formatCurrency(s.amount)}</td>
                    <td className="px-4 py-3">
                      {s.overdue ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Scaduta</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">A scadere</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.openScadenze.length > 30 && (
              <p className="px-4 py-3 text-xs text-neutral-400 border-t border-gray-100">
                Mostrate le prime 30 di {data.openScadenze.length} scadenze.
              </p>
            )}
          </div>
        )}
      </Card>
      </>
      )}
    </div>
  );
}
